<script setup>
import {computed, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import {useComponentsStore} from '@/stores/components'
import {useToastsStore} from '@/stores/toasts'
import {useModalsStore} from '@/stores/modals'
import {useViewStore} from '@/stores/view'
import {useAppStore} from '@/stores/appStore'

import Button from '@/common/Button.vue'
import api from '@/services/api'

const props = defineProps({
    viewId: String,
    showText: Boolean
})

const route = useRoute()

const appStore = useAppStore()
const viewStore = useViewStore(props.viewId)
const componentsStore = useComponentsStore()
const toastsStore = useToastsStore()
const modalsStore = useModalsStore()

const hasVariations = ref(false)

const tableName = computed(() => viewStore.tableName)
const selectedIds = computed(() => viewStore.selectedIds || [])
const selectedIdsLength = computed(() => selectedIds.value.length)
const allSelected = computed(() => viewStore.allSelected)
const allItemsSelected = computed(() => viewStore.allItemsSelected)
const itemCount = computed(() => viewStore.itemCount || 0)
const showArchive = computed(() => viewStore.showArchive || false)
const componentId = computed(() => tableName.value ? componentsStore.componentIds[tableName.value] : undefined)
const productTypeId = computed(() => parseInt(route.query.productTypeId || 0))

const text = computed(() => {
    let count = selectedIdsLength.value
    return props.showText
        ? 'Delete ' + count + ' item' + (count > 1 ? 's' : '')
        : ''
})

const deleteOption = computed(() => {
    if (!componentId.value) return false
    let component = componentsStore.items.find(o => o.id === componentId.value)
    return !!(component && component.deleteOption)
})

const useArchive = computed(() => {
    if (!componentId.value) return false
    let component = componentsStore.items.find(o => o.id === componentId.value)
    return !!(component && component.useArchive)
})

const show = computed(() => {
    return (
        (showArchive.value || useArchive.value === false) &&
        hasVariations.value === false &&
        deleteOption.value &&
        selectedIdsLength.value > 0
    )
})

watch(
    () => [tableName.value, productTypeId.value],
    async ([newTable, newTypeId]) => {
        if (newTable === 'product_options' && newTypeId) {
            try {
                const response = await api.post('api/ProductTypes/hasVariations', {
                    typeId: newTypeId
                })
                hasVariations.value = response && response.data ? response.data.count > 0 : false
            } catch (err) {
                hasVariations.value = false
            }
        } else {
            hasVariations.value = false
        }
    },
    {immediate: true}
)

const deleteAction = async () => {
    const onDelete = () => {
        appStore.cacheNeedsClearing = true
        viewStore.selectNone()
        viewStore.scheduleLoad()
    }

    if (allItemsSelected.value) {
        // If this is a variations table then only delete variations associated to the current product.
        if (tableName.value.indexOf('product_variations__') === 0) {
            const productId = parseInt(route.query.productId || '')

            if (!productId) {
                toastsStore.add({
                    body: 'A product must be specified.'
                })
                return
            }

            try {
                // Load the product's variation IDs.
                const obj = await api.get('api/component/' + tableName.value + '/id', {
                    params: {productId}
                })

                if (obj.data.values.length) {
                    // Submit ids as a post request with X-Http-Method-Override to convert server side to a delete request.
                    await api.post(
                        'api/component/' + tableName.value,
                        {
                            ids: obj.data.values,
                            fastDelete: true,
                        },
                        {
                            headers: {
                                'X-Http-Method-Override': 'DELETE'
                            }
                        }
                    )
                    onDelete()
                } else {
                    onDelete()
                }
            } catch (error) {
                console.error('Failed to perform bulk variation delete:', error)
            }
        } else {
            console.error('Bulk delete for non-variations components not configured.')
        }
    } else {
        try {
            await api.delete('api/component/' + tableName.value, {
                params: {
                    ids: selectedIds.value
                }
            })
            onDelete()
        } catch (error) {
            console.error('Failed to delete selected items:', error)
        }
    }
}

const deleteSelectItems = async () => {
    if (tableName.value === 'product_variables') {
        try {
            // Fetch usage counts for all selected variables concurrently
            const responses = await Promise.all(
                selectedIds.value.map(variableId =>
                    api.get(`api/ProductOptions/getVariablesUsageCount/${variableId}`)
                )
            )

            let msg = ''
            responses.forEach(obj => {
                const data = obj.data
                msg += `${data.count} variation uses ${data.variableTitle}.<br>`
            })

            modalsStore.show({
                componentName: 'ConfirmModal',
                obj: {
                    modalTitle: 'Deleting variables',
                    modalBody: `
                        <div class="alert alert-danger">
                            Deleting variables also deletes the variations which use them.
                            <br><br>
                            ${msg}
                        </div>
                        <p>
                            This action <strong>cannot</strong> be undone.
                        </p>
                    `,
                    confirmStr: 'DELETE',
                    onConfirm: () => {
                        deleteAction()
                    }
                }
            })
        } catch (error) {
            console.error('Failed to check variable usage counts:', error)
        }
    } else if (selectedIdsLength.value) {
        const numItems = allItemsSelected.value ? itemCount.value : selectedIdsLength.value

        modalsStore.show({
            componentName: 'ConfirmModal',
            obj: {
                modalTitle: `Deleting ${numItems} item${numItems > 1 ? 's' : ''}`,
                modalBody: `
                    <div class="alert alert-danger">
                        This action will result in loss of data.
                    </div>
                    <p>
                        This action <strong>cannot</strong> be undone.
                    </p>
                `,
                onConfirm: () => {
                    deleteAction()
                }
            }
        })
    }
}
</script>

<template>
    <Button v-if="show && showText" class="d-flex align-items-center" @click="deleteSelectItems">
        <i class="bi-trash3 me-2"/>
        {{ text }}
    </Button>

    <Button v-else-if="show" @click="deleteSelectItems">
        <i class="bi-trash3"/>
        {{ text }}
    </Button>
</template>